/**
 * Browser-notification helper for subscription reminders.
 *
 * Strategy: pragmatic, server-less. On dashboard load, the client fetches
 * /api/notifications/upcoming-subscriptions and uses the browser's
 * Notification API (via the Service Worker registration if available, else
 * directly) to display reminders. A localStorage de-dup key prevents the
 * same reminder from firing twice per day per subscription.
 */
import { getUpcomingSubscriptionNotifications } from './api';

const STORAGE_KEY = 'spenspot:subscription_reminders_sent';

function getSentToday() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    const today = new Date().toISOString().slice(0, 10);
    // Clear old entries
    const fresh = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v === today) fresh[k] = v;
    });
    return fresh;
  } catch {
    return {};
  }
}

function markSent(key) {
  const sent = getSentToday();
  sent[key] = new Date().toISOString().slice(0, 10);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(sent)); } catch { /* ignore */ }
}

async function showNotification(title, options) {
  if (!('Notification' in window)) return false;
  if (Notification.permission !== 'granted') return false;
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        await reg.showNotification(title, options);
        return true;
      }
    }
    // Fallback: page-level notification
    /* eslint-disable no-new */
    new Notification(title, options);
    return true;
  } catch (e) {
    console.warn('Could not show notification', e);
    return false;
  }
}

/**
 * Request permission once (idempotent). Call from a user gesture if needed.
 */
export async function ensureNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'default') {
    try {
      return await Notification.requestPermission();
    } catch {
      return Notification.permission;
    }
  }
  return Notification.permission;
}

/**
 * Check for upcoming subscription payments and fire browser notifications.
 * Idempotent within a single calendar day (per subscription).
 *
 * @param {Object} opts
 * @param {string} opts.language - 'fr' | 'en' | 'pt'
 */
export async function runSubscriptionReminders({ language = 'fr' } = {}) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  let data;
  try {
    const res = await getUpcomingSubscriptionNotifications();
    data = res.data;
  } catch (e) {
    return;
  }
  if (!data?.upcoming_subscriptions?.length) return;

  const sent = getSentToday();
  for (const sub of data.upcoming_subscriptions) {
    const dedupKey = `sub_${sub.subscription_id}`;
    if (sent[dedupKey]) continue;
    const daysUntil = sub.days_until;
    const amount = sub.amount;
    const title = language === 'fr'
      ? `📅 ${sub.name} bientôt prélevé`
      : language === 'pt'
        ? `📅 ${sub.name} em breve`
        : `📅 ${sub.name} due soon`;
    const body = daysUntil === 0
      ? (language === 'fr'
        ? `${amount.toFixed(2)}€ seront prélevés aujourd'hui.`
        : language === 'pt'
          ? `${amount.toFixed(2)}€ serão cobrados hoje.`
          : `${amount.toFixed(2)}€ will be charged today.`)
      : (language === 'fr'
        ? `${amount.toFixed(2)}€ dans ${daysUntil} jour${daysUntil > 1 ? 's' : ''} (${sub.next_payment_date}).`
        : language === 'pt'
          ? `${amount.toFixed(2)}€ em ${daysUntil} dia${daysUntil > 1 ? 's' : ''} (${sub.next_payment_date}).`
          : `${amount.toFixed(2)}€ in ${daysUntil} day${daysUntil > 1 ? 's' : ''} (${sub.next_payment_date}).`);

    const ok = await showNotification(title, {
      body,
      tag: `subscription-${sub.subscription_id}`,
      icon: '/icons/icon-192x192.svg',
      badge: '/icons/icon-96x96.svg',
      data: { type: 'subscription_reminder', subscription_id: sub.subscription_id },
    });
    if (ok) markSent(dedupKey);
  }
}
